import { ECMR } from '../types/eCMR';
import fileDownload from 'js-file-download';
import { validateECMR } from './schemaValidator';
import { authService } from './authService';

// Template para un eCMR vacío
export const emptyECMR: ECMR = {
  ExchangedDocumentContext: {
    GuidelineSpecifiedDocumentContextParameter: [
      {
        ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A',
      }
    ]
  },
  ExchangedDocument: {
    ID: '',
    TypeCode: '730', // Código para CMR
    IssueDateTime: {
      DateTimeString: new Date().toISOString().replace(/[-:]/g, '').substring(0, 14),
    },
    Remarks: [],
    IncludedNote: [],
    IssueLogisticsLocation: {
      Name: '',
    },
    IssuerTradeParty: {
      ID: [''],
      Name: [''],
      DefinedTradeContact: []
    }
  },
  SpecifiedSupplyChainConsignment: {
    ID: '',
    ConsignorAssignedID: '',
    CarrierAssignedID: '',
    GrossWeightMeasure: [],
    TransshipmentPermissionIndicator: false,
    ConsignorTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    ConsigneeTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    CarrierTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    CarrierAcceptanceLogisticsLocation: {
      Name: '',
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      }
    },
    ConsigneeReceiptLogisticsLocation: {
      Name: '',
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      }
    },
    IncludedSupplyChainConsignmentItem: [],
    UtilizedLogisticsTransportEquipment: [],
    MainCarriageLogisticsTransportMovement: []
  }
};

// Convertir objeto ECMR a XML (simplified version using template strings)
export const convertToXml = async (ecmr: ECMR): Promise<string> => {
  // Simplified XML generation using template strings
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rsm:eCMR 
  xmlns:rsm="urn:un:unece:uncefact:data:standard:eCMR:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100"
  xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="urn:un:unece:uncefact:data:standard:eCMR:100 eCMR_100pD24A.xsd">
  
  <ExchangedDocumentContext>
    <GuidelineSpecifiedDocumentContextParameter>
      <ID>${ecmr.ExchangedDocumentContext?.GuidelineSpecifiedDocumentContextParameter?.[0]?.ID || 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A'}</ID>
    </GuidelineSpecifiedDocumentContextParameter>
  </ExchangedDocumentContext>
  
  <ExchangedDocument>
    <ID>${ecmr.ExchangedDocument?.ID || ''}</ID>
    <TypeCode>${ecmr.ExchangedDocument?.TypeCode || '730'}</TypeCode>
    <IssueDateTime>
      <DateTimeString>${ecmr.ExchangedDocument?.IssueDateTime?.DateTimeString || ''}</DateTimeString>
    </IssueDateTime>
  </ExchangedDocument>
  
  <SpecifiedSupplyChainConsignment>
    <ID>${ecmr.SpecifiedSupplyChainConsignment?.ID || ''}</ID>
    <ConsignorAssignedID>${ecmr.SpecifiedSupplyChainConsignment?.ConsignorAssignedID || ''}</ConsignorAssignedID>
  </SpecifiedSupplyChainConsignment>
  
</rsm:eCMR>`;

  return xml;
};

// Convertir XML a objeto ECMR (using native DOM parser)
export const convertFromXml = async (xml: string): Promise<ECMR> => {
  // Validar el XML primero
  const validationResult = await validateECMR(xml);
  if (!validationResult.valid && validationResult.errors) {
    // Si hay errores, los enviamos como advertencias pero continuamos la conversión
    console.warn('Advertencias de validación:', validationResult.errors);
  }
  
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, 'text/xml');
  
  // Check for parsing errors
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Error parsing XML: ' + parseError.textContent);
  }
  
  return transformXmlToEcmr(xmlDoc);
};

// Transformar el DOM XML a la estructura ECMR esperada
const transformXmlToEcmr = (xmlDoc: Document): ECMR => {
  // Helper function to get text content
  const getTextContent = (selector: string): string => {
    const element = xmlDoc.querySelector(selector);
    return element?.textContent?.trim() || '';
  };

  // Extract basic document information
  const ecmr: ECMR = {
    ExchangedDocumentContext: {
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID: getTextContent('GuidelineSpecifiedDocumentContextParameter ID') || 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A'
        }
      ]
    },
    ExchangedDocument: {
      ID: getTextContent('ExchangedDocument ID'),
      TypeCode: getTextContent('ExchangedDocument TypeCode') || '730',
      IssueDateTime: {
        DateTimeString: getTextContent('ExchangedDocument IssueDateTime DateTimeString')
      },
      Remarks: [],
      IncludedNote: [],
      IssueLogisticsLocation: {
        Name: getTextContent('IssueLogisticsLocation Name')
      },
      IssuerTradeParty: {
        ID: [getTextContent('IssuerTradeParty ID')],
        Name: [getTextContent('IssuerTradeParty Name')],
        DefinedTradeContact: []
      }
    },
    SpecifiedSupplyChainConsignment: {
      ID: getTextContent('SpecifiedSupplyChainConsignment ID'),
      ConsignorAssignedID: getTextContent('SpecifiedSupplyChainConsignment ConsignorAssignedID'),
      CarrierAssignedID: getTextContent('SpecifiedSupplyChainConsignment CarrierAssignedID') || '',
      GrossWeightMeasure: [],
      TransshipmentPermissionIndicator: false,
      ConsignorTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      ConsigneeTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      CarrierTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      CarrierAcceptanceLogisticsLocation: {
        Name: '',
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        }
      },
      ConsigneeReceiptLogisticsLocation: {
        Name: '',
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        }
      },
      IncludedSupplyChainConsignmentItem: [],
      UtilizedLogisticsTransportEquipment: [],
      MainCarriageLogisticsTransportMovement: []
    }
  };

  return ecmr;
};

// Guardar el XML como archivo
export const saveECMRAsXml = async (ecmr: ECMR, filename: string = 'ecmr.xml'): Promise<void> => {
  try {
    const xml = await convertToXml(ecmr);
    fileDownload(xml, filename);
  } catch (error) {
    console.error('Error al guardar el XML:', error);
    throw error;
  }
};

// Cargar XML desde un archivo
export const loadECMRFromXml = async (file: File): Promise<{ ecmr: ECMR, validationResult?: { valid: boolean, errors?: string[] } }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const xml = e.target?.result as string;
        // Validar el XML
        const validationResult = await validateECMR(xml);
        const ecmr = await convertFromXml(xml);
        resolve({ ecmr, validationResult });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };
    reader.readAsText(file);
  });
};

// Ejemplos para crear y manipular eCMRs
export const createExampleECMR = (): ECMR => {
  const now = new Date();
  const formattedDateTime = now.toISOString().replace(/[-:]/g, '').substring(0, 14);

  return {
    ExchangedDocumentContext: {
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A'
        }
      ]
    },
    ExchangedDocument: {
      ID: `CMR-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`,
      TypeCode: '730',
      IssueDateTime: {
        DateTimeString: formattedDateTime
      },
      Remarks: ['Entrega urgente'],
      IssueLogisticsLocation: {
        Name: 'Madrid'
      },
      IssuerTradeParty: {
        ID: ['ES12345678Z'],
        Name: ['Empresa Exportadora S.A.'],
        DefinedTradeContact: [
          {
            PersonName: ['Juan Pérez'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+34912345678'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'juan.perez@empresaexportadora.es'
            }
          }
        ]
      }
    },
    SpecifiedSupplyChainConsignment: {
      ID: `CONS-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}-${now.getFullYear()}`,
      ConsignorAssignedID: `CS-REF-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      CarrierAssignedID: `CAR-REF-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      GrossWeightMeasure: [
        {
          value: '1250.00',
          unitCode: 'KGM'
        }
      ],
      GrossVolumeMeasure: [
        {
          value: '2.75',
          unitCode: 'MTQ'
        }
      ],
      TransshipmentPermissionIndicator: true,
      ConsignorTradeParty: {
        ID: ['ES12345678Z'],
        Name: ['Empresa Exportadora S.A.'],
        PostalTradeAddress: {
          PostcodeCode: '28001',
          StreetName: ['Calle Gran Vía 123'],
          CityName: ['Madrid'],
          CountryID: 'ES'
        },
        DefinedTradeContact: [
          {
            PersonName: ['Juan Pérez'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+34912345678'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'juan.perez@empresaexportadora.es'
            }
          }
        ]
      },
      ConsigneeTradeParty: {
        ID: ['FR87654321X'],
        Name: ['Entreprise Importatrice SARL'],
        PostalTradeAddress: {
          PostcodeCode: '75001',
          StreetName: ['Avenue des Champs-Élysées 456'],
          CityName: ['Paris'],
          CountryID: 'FR'
        },
        DefinedTradeContact: [
          {
            PersonName: ['Marie Dupont'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+33123456789'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'marie.dupont@importatrice.fr'
            }
          }
        ]
      },
      CarrierTradeParty: {
        ID: ['PT98765432Y'],
        Name: ['Transportes Internacionales S.L.'],
        PostalTradeAddress: {
          PostcodeCode: '4000-123',
          StreetName: ['Rua do Comercio 789'],
          CityName: ['Porto'],
          CountryID: 'PT'
        },
        DefinedTradeContact: [
          {
            PersonName: ['António Silva'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+351987654321'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'antonio.silva@transportes.pt'
            }
          }
        ]
      },
      CarrierAcceptanceLogisticsLocation: {
        Name: 'Almacén Central Madrid',
        Description: 'Zona de carga puerta 5',
        PhysicalGeographicalCoordinate: {
          LatitudeMeasure: '40.4168',
          LongitudeMeasure: '-3.7038'
        },
        PostalTradeAddress: {
          PostcodeCode: '28042',
          StreetName: ['Calle Mercancías 55'],
          CityName: ['Madrid'],
          CountryID: 'ES'
        }
      },
      ConsigneeReceiptLogisticsLocation: {
        Name: 'Entrepôt Central Paris',
        Description: 'Zone de déchargement porte 3',
        PhysicalGeographicalCoordinate: {
          LatitudeMeasure: '48.8566',
          LongitudeMeasure: '2.3522'
        },
        PostalTradeAddress: {
          PostcodeCode: '75015',
          StreetName: ['Rue de Commerce 123'],
          CityName: ['Paris'],
          CountryID: 'FR'
        }
      },
      AssociatedReferencedDocument: [
        {
          ID: `INV-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
          TypeCode: '380',
          FormattedIssueDateTime: {
            DateTimeString: now.toISOString().substring(0, 10).replace(/-/g, '')
          }
        }
      ],
      HandlingInstructions: [
        {
          Description: ['Manipular con cuidado. Producto frágil.']
        }
      ],
      PickUpTransportEvent: {
        ScheduledOccurrenceDateTime: {
          DateTimeString: formattedDateTime
        }
      },
      DeliveryTransportEvent: {
        ScheduledOccurrenceDateTime: {
          DateTimeString: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000)
            .toISOString()
            .replace(/[-:]/g, '')
            .substring(0, 14)
        }
      },
      IncludedSupplyChainConsignmentItem: [
        {
          SequenceNumeric: 1,
          GrossWeightMeasure: {
            value: '750.00',
            unitCode: 'KGM'
          },
          TransportLogisticsPackage: [
            {
              ID: 'PKG-001',
              TypeCode: 'PK',
              ItemQuantity: 25,
              Description: 'Cajas de componentes electrónicos'
            }
          ]
        },
        {
          SequenceNumeric: 2,
          GrossWeightMeasure: {
            value: '500.00',
            unitCode: 'KGM'
          },
          TransportLogisticsPackage: [
            {
              ID: 'PKG-002',
              TypeCode: 'CT',
              ItemQuantity: 10,
              Description: 'Cartones de equipamiento industrial'
            }
          ]
        }
      ],
      UtilizedLogisticsTransportEquipment: [
        {
          ID: 'ES-2345-ABC',
          CategoryCode: '31',
          CharacteristicCode: '6',
          OperatingPartyTradeParty: {
            Name: ['Transportes Internacionales S.L.'],
            ID: ['PT98765432Y']
          }
        }
      ],
      MainCarriageLogisticsTransportMovement: [
        {
          ID: 'ROUTE-ES-FR-001',
          ModeCode: '30',
          UsedLogisticsTransportMeans: {
            ID: 'ES-2345-ABC',
            TypeCode: '165',
            Name: 'Scania R450'
          },
          DriverPerson: {
            ID: 'LIC-123456789',
            Name: 'Carlos Rodríguez'
          }
        }
      ],
      SpecifiedTradeDeliveryTerms: {
        DeliveryTypeCode: 'DAP'
      }
    }
  };
};

// Storage utility functions
export const saveECMRToLocalStorage = (ecmr: ECMR, key: string = 'savedECMR'): void => {
  localStorage.setItem(key, JSON.stringify(ecmr));
};

export const loadECMRFromLocalStorage = (key: string = 'savedECMR'): ECMR | null => {
  const savedECMR = localStorage.getItem(key);
  if (savedECMR) {
    return JSON.parse(savedECMR) as ECMR;
  }
  return null;
};

export const getAllSavedECMRKeys = (): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('ecmr_')) {
      keys.push(key);
    }
  }
  return keys;
};

export const saveECMR = (ecmr: ECMR): string => {
  const id = ecmr.ExchangedDocument?.ID || `ecmr_${Date.now()}`;
  const key = `ecmr_${id}`;
  saveECMRToLocalStorage(ecmr, key);
  return key;
};

export const getECMRList = (): { id: string, title: string, date: string }[] => {
  const keys = getAllSavedECMRKeys();
  return keys.map(key => {
    const ecmr = loadECMRFromLocalStorage(key);
    return {
      id: key,
      title: ecmr?.ExchangedDocument?.ID || 'Sin título',
      date: ecmr?.ExchangedDocument?.IssueDateTime?.DateTimeString || 'Sin fecha'
    };
  });
};

// Función para asegurarse de que todas las partes importantes de un ECMR existen
export const ensureECMRStructure = (ecmr: ECMR): ECMR => {
  if (!ecmr.ExchangedDocumentContext) {
    ecmr.ExchangedDocumentContext = {
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A',
        }
      ]
    };
  }

  // Asegurar que ExchangedDocument existe
  if (!ecmr.ExchangedDocument) {
    ecmr.ExchangedDocument = {
      ID: '',
      TypeCode: '730',
      IssueDateTime: {
        DateTimeString: new Date().toISOString().replace(/[-:]/g, '').substring(0, 14)
      },
      Remarks: [],
      IncludedNote: [],
      IssueLogisticsLocation: {
        Name: ''
      },
      IssuerTradeParty: {
        ID: [''],
        Name: [''],
        DefinedTradeContact: []
      }
    };
  } else {
    // Asegurar que IssueDateTime existe
    if (!ecmr.ExchangedDocument.IssueDateTime) {
      ecmr.ExchangedDocument.IssueDateTime = {
        DateTimeString: new Date().toISOString().replace(/[-:]/g, '').substring(0, 14)
      };
    }
    
    // Asegurar que Remarks existe
    if (!ecmr.ExchangedDocument.Remarks) {
      ecmr.ExchangedDocument.Remarks = [];
    }
  }

  // Asegurar que las partes esenciales de la consignación existen
  if (!ecmr.SpecifiedSupplyChainConsignment) {
    ecmr.SpecifiedSupplyChainConsignment = {
      ID: '',
      ConsignorAssignedID: '',
      CarrierAssignedID: '',
      GrossWeightMeasure: [],
      TransshipmentPermissionIndicator: false,
      ConsignorTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      ConsigneeTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      CarrierTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      IncludedSupplyChainConsignmentItem: [],
      UtilizedLogisticsTransportEquipment: [],
      MainCarriageLogisticsTransportMovement: []
    };
  } else {
    // Asegurar que ConsignorTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }

    // Asegurar que ConsigneeTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }

    // Asegurar que CarrierTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }
    
    // Asegurar que los arrays existen
    if (!ecmr.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem) {
      ecmr.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem = [];
    }
    
    if (!ecmr.SpecifiedSupplyChainConsignment.UtilizedLogisticsTransportEquipment) {
      ecmr.SpecifiedSupplyChainConsignment.UtilizedLogisticsTransportEquipment = [];
    }
    
    if (!ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement) {
      ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement = [];
    }
  }

  return ecmr;
};

// Función para generar y cargar varios ejemplos de documentos eCMR
export const loadExampleECMRs = (): string[] => {
  const savedKeys: string[] = [];
  
  try {
    // Ejemplo 1: Transporte de mercancías general
    let example1 = createExampleECMR();
    example1 = ensureECMRStructure(example1);
    
    if (example1.ExchangedDocument) {
      example1.ExchangedDocument.ID = `CMR-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
      example1.ExchangedDocument.Remarks = ['Transporte estándar de mercancías mixtas'];
    }
    
    if (example1.SpecifiedSupplyChainConsignment.ConsignorTradeParty) {
      example1.SpecifiedSupplyChainConsignment.ConsignorTradeParty.Name = ['Electrónica Europa S.A.'];
    }
    
    if (example1.SpecifiedSupplyChainConsignment.ConsigneeTradeParty) {
      example1.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.Name = ['Distribuidora Tecnológica FR'];
    }
    
    savedKeys.push(saveECMR(example1));
    
    // Ejemplo 2: Transporte internacional de productos perecederos
    let example2 = createExampleECMR();
    example2 = ensureECMRStructure(example2);
    
    if (example2.ExchangedDocument) {
      example2.ExchangedDocument.ID = `CMR-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
      example2.ExchangedDocument.Remarks = ['Productos perecederos - Mantener refrigerado'];
    }
    
    if (example2.SpecifiedSupplyChainConsignment.ConsignorTradeParty) {
      example2.SpecifiedSupplyChainConsignment.ConsignorTradeParty.Name = ['Frutas del Sur S.L.'];
      example2.SpecifiedSupplyChainConsignment.ConsignorTradeParty.ID = ['ES98765432B'];
      example2.SpecifiedSupplyChainConsignment.ConsignorTradeParty.PostalTradeAddress = {
        PostcodeCode: '29001',
        StreetName: ['Avenida de los Cítricos 45'],
        CityName: ['Málaga'],
        CountryID: 'ES'
      };
    }
    
    if (example2.SpecifiedSupplyChainConsignment.ConsigneeTradeParty) {
      example2.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.Name = ['Marché Frais SARL'];
      example2.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.ID = ['FR87654321Y'];
      example2.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.PostalTradeAddress = {
        PostcodeCode: '69001',
        StreetName: ['Rue du Marché 123'],
        CityName: ['Lyon'],
        CountryID: 'FR'
      };
    }
    
    if (example2.SpecifiedSupplyChainConsignment.CarrierTradeParty) {
      example2.SpecifiedSupplyChainConsignment.CarrierTradeParty.Name = ['Transportes Rápidos S.A.'];
    }
    
    example2.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms = {
      DeliveryTypeCode: 'DAP'
    };
    
    example2.SpecifiedSupplyChainConsignment.HandlingInstructions = [{
      Description: ['Mantener a temperatura entre 2°C y 6°C. No apilar más de 3 cajas.']
    }];
    
    // Añadir información específica de mercancías refrigeradas
    example2.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem = [
      {
        SequenceNumeric: 1,
        GrossWeightMeasure: {
          value: '850.00',
          unitCode: 'KGM'
        },
        TransportLogisticsPackage: [
          {
            ID: 'PKG-FR-001',
            TypeCode: 'PK',
            ItemQuantity: 40,
            Description: 'Cajas de naranjas'
          }
        ]
      },
      {
        SequenceNumeric: 2,
        GrossWeightMeasure: {
          value: '620.00',
          unitCode: 'KGM'
        },
        TransportLogisticsPackage: [
          {
            ID: 'PKG-FR-002',
            TypeCode: 'PK',
            ItemQuantity: 30,
            Description: 'Cajas de limones'
          }
        ]
      }
    ];
    
    savedKeys.push(saveECMR(example2));
    
    // Ejemplo 3: Transporte de productos peligrosos
    let example3 = createExampleECMR();
    example3 = ensureECMRStructure(example3);
    
    if (example3.ExchangedDocument) {
      example3.ExchangedDocument.ID = `CMR-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
      example3.ExchangedDocument.Remarks = ['Productos químicos - ADR Clase 3'];
    }
    
    if (example3.SpecifiedSupplyChainConsignment.ConsignorTradeParty) {
      example3.SpecifiedSupplyChainConsignment.ConsignorTradeParty.Name = ['Química Industrial GmbH'];
      example3.SpecifiedSupplyChainConsignment.ConsignorTradeParty.ID = ['DE43219876Z'];
      example3.SpecifiedSupplyChainConsignment.ConsignorTradeParty.PostalTradeAddress = {
        PostcodeCode: '50667',
        StreetName: ['Industriestrasse 78'],
        CityName: ['Colonia'],
        CountryID: 'DE'
      };
    }
    
    if (example3.SpecifiedSupplyChainConsignment.ConsigneeTradeParty) {
      example3.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.Name = ['Industrias Químicas Ibéricas S.A.'];
      example3.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.ID = ['ES12345678Z'];
      example3.SpecifiedSupplyChainConsignment.ConsigneeTradeParty.PostalTradeAddress = {
        PostcodeCode: '08001',
        StreetName: ['Calle Industria 234'],
        CityName: ['Barcelona'],
        CountryID: 'ES'
      };
    }
    
    if (example3.SpecifiedSupplyChainConsignment.CarrierTradeParty) {
      example3.SpecifiedSupplyChainConsignment.CarrierTradeParty.Name = ['ADR Transport Specialists S.L.'];
      example3.SpecifiedSupplyChainConsignment.CarrierTradeParty.ID = ['ES87654321X'];
    }
    
    example3.SpecifiedSupplyChainConsignment.HandlingInstructions = [{
      Description: ['Mercancía peligrosa - Inflamable. Mantener alejado de fuentes de calor/ignición.']
    }];
    
    // Información específica de mercancías peligrosas
    example3.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem = [
      {
        SequenceNumeric: 1,
        GrossWeightMeasure: {
          value: '450.00',
          unitCode: 'KGM'
        },
        TransportLogisticsPackage: [
          {
            ID: 'PKG-DR-001',
            TypeCode: 'DR',
            ItemQuantity: 10,
            Description: 'Bidones de acetona (UN 1090)'
          }
        ]
      },
      {
        SequenceNumeric: 2,
        GrossWeightMeasure: {
          value: '550.00',
          unitCode: 'KGM'
        },
        TransportLogisticsPackage: [
          {
            ID: 'PKG-DR-002',
            TypeCode: 'DR',
            ItemQuantity: 12,
            Description: 'Bidones de tolueno (UN 1294)'
          }
        ]
      }
    ];
    
    savedKeys.push(saveECMR(example3));
    
    return savedKeys;
  } catch (error) {
    console.error("Error al cargar ejemplos:", error);
    return savedKeys;
  }
};

// API Integration Functions
export interface ApiDocument {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

// Get documents from API
export const getDocumentsFromAPI = async (): Promise<ApiDocument[]> => {
  try {
    const response = await authService.authenticatedRequest('/documents');
    
    if (!response.ok) {
      throw new Error('Error al obtener documentos de la API');
    }
    
    const data = await response.json();
    return data.documents || [];
  } catch (error) {
    console.error('Error fetching documents from API:', error);
    throw error;
  }
};

// Save document to API
export const saveDocumentToAPI = async (ecmr: ECMR, title?: string): Promise<ApiDocument> => {
  try {
    const xmlContent = await convertToXml(ecmr);
    const documentTitle = title || ecmr.ExchangedDocument?.ID || `eCMR-${Date.now()}`;
    
    const response = await authService.authenticatedRequest('/documents', {
      method: 'POST',
      body: JSON.stringify({
        title: documentTitle,
        content: xmlContent,
        type: 'ecmr'
      }),
    });
    
    if (!response.ok) {
      throw new Error('Error al guardar documento en la API');
    }
    
    const data = await response.json();
    return data.document;
  } catch (error) {
    console.error('Error saving document to API:', error);
    throw error;
  }
};

// Get specific document from API
export const getDocumentFromAPI = async (documentId: string): Promise<ApiDocument> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}`);
    
    if (!response.ok) {
      throw new Error('Error al obtener documento de la API');
    }
    
    const data = await response.json();
    return data.document;
  } catch (error) {
    console.error('Error fetching document from API:', error);
    throw error;
  }
};

// Convert API document to ECMR
export const convertApiDocumentToECMR = async (apiDocument: ApiDocument): Promise<ECMR> => {
  try {
    return await convertFromXml(apiDocument.content);
  } catch (error) {
    console.error('Error converting API document to ECMR:', error);
    throw error;
  }
};

// Enhanced getECMRList that combines local and API documents
export const getEnhancedECMRList = async (): Promise<{ id: string, title: string, date: string, source: 'local' | 'api' }[]> => {
  const combinedList: { id: string, title: string, date: string, source: 'local' | 'api' }[] = [];
  
  // Always get local documents
  const localList = getECMRList();
  combinedList.push(...localList.map(item => ({ ...item, source: 'local' as const })));
  
  // Get API documents only if authenticated (optional)
  try {
    if (authService.isAuthenticated()) {
      const apiDocuments = await getDocumentsFromAPI();
      const apiList = apiDocuments.map(doc => ({
        id: doc.id,
        title: doc.title,
        date: new Date(doc.created_at).toLocaleDateString(),
        source: 'api' as const
      }));
      combinedList.push(...apiList);
    }
  } catch (error) {
    console.warn('Could not fetch API documents (working offline):', error);
  }
  
  // Sort by date (newest first)
  return combinedList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

// Save document with optional cloud sync
export const saveECMRWithOptionalSync = async (ecmr: ECMR, title?: string): Promise<{ localKey: string, apiDocument?: ApiDocument }> => {
  // Always save locally first
  const localKey = saveECMR(ecmr);
  
  let apiDocument: ApiDocument | undefined;
  
  // Try to save to API if authenticated
  try {
    if (authService.isAuthenticated()) {
      apiDocument = await saveDocumentToAPI(ecmr, title);
    }
  } catch (error) {
    console.warn('Could not save to cloud (saved locally):', error);
  }
  
  return { localKey, apiDocument };
}; 